import { ToolkitApiService } from '@jeppesen-foreflight/jmkw-toolkit';
import { NGXLogger } from 'ngx-logger';
import { JepLayer } from '@jeppesen-foreflight/jmkw-types';

/**
 * Generic helper to replace a map layer while preserving a subset of state.
 * - Preserves visibility, zOrder, and whitelisted custom properties (e.g. IFR flag)
 * - Updates the plugin child layer reference when provided
 */
export async function replaceLayerPreservingState<
  TLayer extends JepLayer
>(params: {
  api: ToolkitApiService;
  logger: NGXLogger;
  currentLayer: TLayer | undefined;
  createReplacement: () => TLayer; // factory for new layer instance
  stateTransfer?: (src: TLayer | undefined, dest: TLayer) => void; // optional additional state transfer
  pluginId?: string; // parent plugin id (e.g. 'aviation')
  childId?: string; // child plugin id (e.g. 'navigation')
}): Promise<TLayer> {
  const {
    api,
    logger,
    currentLayer,
    createReplacement,
    stateTransfer,
    pluginId,
    childId
  } = params;

  const replacement = createReplacement();
  try {
    if (currentLayer) {
      // Basic preserved state
      replacement.visible = currentLayer.visible;
      if (currentLayer.zOrder !== undefined) {
        replacement.zOrder = currentLayer.zOrder;
      }
      // Custom transfer
      stateTransfer?.(currentLayer, replacement);
    }

    const { newLayer } = api.replaceLayer(replacement, ['id']);
    const finalLayer = (newLayer as TLayer) ?? replacement;

    if (pluginId && childId) {
      const plugin = api.getPluginById(pluginId)?.children?.get(childId);
      if (plugin) plugin.layer = finalLayer;
    }

    // If the layer supports async load() perform it now
    if (typeof finalLayer.load === 'function') {
      await finalLayer.load();
    }

    return finalLayer;
  } catch (err) {
    logger.error(
      `[LayerReplace] Failed replacing layer '${currentLayer?.id}' -> '${replacement.id}'`,
      err
    );
    return (currentLayer as TLayer) ?? replacement;
  }
}
